import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { TypedMemo } from '../../common/types';
import { config } from '../../../config/config';
import { isAnalyticsPaused } from './analyticsPause';

const SCRIPT_ID = 'plausible-script';

/**
 * Mounts the Plausible analytics script and fires a manual `pageview`
 * event whenever the SPA route changes. Mirrors the music24-website
 * setup (uses `script.manual.js` so we control when pageviews fire).
 *
 * No-op when `config.plausible` is null (any non-production env).
 */
const PlausibleTrackerComponent: React.FC = () => {
  const location = useLocation();
  const lastTrackedRef = useRef<string | null>(null);

  // One-time bootstrap: prime the queue + inject the script.
  useEffect(() => {
    const plausibleConfig = config.plausible;
    if (plausibleConfig === null) {
      return;
    }
    if (document.getElementById(SCRIPT_ID) !== null) {
      return;
    }
    // Queue stub so calls before the script loads aren't lost.
    if (window.plausible === undefined) {
      const stub = ((...args: unknown[]) => {
        if (window.plausible !== undefined) {
          (window.plausible.q = window.plausible.q ?? []).push(args);
        }
      }) as Window['plausible'];
      window.plausible = stub;
    }
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.defer = true;
    script.src = `${plausibleConfig.url}js/script.manual.js`;
    script.setAttribute('data-domain', plausibleConfig.siteId);
    document.head.appendChild(script);
  }, []);

  // Fire pageview on every SPA route change (and the initial route).
  useEffect(() => {
    // No page view of an admin session ("Log in as this user").
    if (config.plausible === null || isAnalyticsPaused()) {
      return;
    }
    const url = `${location.pathname}${location.search}${location.hash}`;
    if (lastTrackedRef.current === url) {
      return;
    }
    lastTrackedRef.current = url;
    window.plausible?.('pageview');
  }, [location.pathname, location.search, location.hash]);

  return null;
};

export const PlausibleTracker = TypedMemo(PlausibleTrackerComponent);
